import { computed, reactive, readonly } from "vue";
import { api, session, setUnauthorizedHandler } from "../api.js";

/**
 * Authentication state.
 *
 * Role comes from the server and is never selectable in the client — a role
 * picker was the original privilege-escalation hole (docs/audit, G1.2).
 *
 * A restored session is provisional until the API accepts the token: a forged
 * value in localStorage must not keep a workspace on screen.
 */
const state = reactive({
  user: session.token ? session.user : null,
  status: session.token ? "restoring" : "anonymous",
  error: null,
  pending: false,
  // Brand profile from /auth/me — the runtime settings (M13) an admin can change.
  app: null
});

export const sessionState = readonly(state);
export const isAuthenticated = computed(() => Boolean(state.user));
export const currentRole = computed(() => state.user?.role || null);
export const appConfig = computed(() => state.app);

/**
 * The live sign-in challenge, held in memory only.
 *
 * Deliberately not persisted: it is a credential, and one that should not
 * survive a reload. Losing it on refresh sends the user back to the password
 * form, which is the correct outcome.
 */
const challenge = reactive({ token: null, purpose: null, message: null });
export const mfaChallenge = readonly(challenge);

export function clearChallenge() {
  challenge.token = null;
  challenge.purpose = null;
  challenge.message = null;
}

export async function signIn(email, password, remember = true) {
  state.pending = true;
  state.error = null;
  try {
    const result = await api.login(email, password, remember);

    if (result.mfaRequired) {
      challenge.token = result.challenge;
      challenge.purpose = result.purpose;
      challenge.message = result.message;
      return result;
    }

    state.user = result.user;
    state.status = "ready";
    return result;
  } catch (error) {
    state.error = error?.status === 401
      ? "Email or password is incorrect, or the account is not yet approved."
      : error?.message || "Sign in failed. Please try again.";
    throw error;
  } finally {
    state.pending = false;
  }
}

export async function signOut() {
  await api.logout();
  clearSession();
}

export function clearSession(message = null) {
  state.user = null;
  state.status = "anonymous";
  state.error = message;
}

/** Adopt the session produced by completing an MFA challenge. */
export function adoptSession(user) {
  clearChallenge();
  state.user = user;
  state.status = "ready";
  state.error = null;
}

export function setSessionError(message) {
  state.error = message;
}

let restoring = null;

/** Await whatever restoration is in flight; resolves immediately when settled. */
export function sessionReady() {
  return restoring || Promise.resolve();
}

export function verifyRestoredSession() {
  restoring = restoreFromToken().finally(() => { restoring = null; });
  return restoring;
}

/**
 * Resolve a restored token into an identity, server-side.
 *
 * The cached user in localStorage is only there so the shell can paint without
 * waiting for a round trip. It is not evidence of anything — anything running on
 * the page can write to it — so the server is asked who the token belongs to and
 * its answer replaces the cache.
 */
async function restoreFromToken() {
  if (!session.token) {
    clearSession();
    return;
  }
  try {
    const { user, app } = await api.me();
    state.user = user;
    state.app = app;
    session.save(session.token, user);
    state.status = "ready";
  } catch (error) {
    // A 401 means the token is dead; the unauthorized handler has already
    // cleared the session. Network trouble is not an authentication failure, so
    // a cached identity survives it rather than logging the user out on a blip.
    if (error?.status === 401) return;
    state.status = state.user ? "ready" : "anonymous";
  }
}

setUnauthorizedHandler(() => clearSession("Your session has ended. Please sign in again."));
